import { useState } from "react";
import type { AppProps } from "next/app";

// chakra compponents
import { ChakraProvider, Box, Center } from "@chakra-ui/react";
import { Dict } from "@chakra-ui/utils";
import { MobileNotSupported, Page as PageTemplate, SupportButton } from "ui";

//  components
import { Header } from "components/Header/Header";
import ConnectWalletDropdown from "components/ConnectWalletDropdown/ConnectWalletDropdown";
import HandleThemeChange from "components/HandleThemeChange";
import ExchangeInfo from "components/ExchangeInfo";
import Socials from "components/Socials";
import BannerWrapper from "components/BannerWrapper/BannerWrapper";

// hashconnect
import { HashConnect } from "hashconnect";
import { HashConnectService } from "services/hash-connect.service";
import HashConnectProvider from "../context/HashConnectProvider";
import TransactionFeeProvider from "../context/TransactionFeeProvider";

// firebase utils
import "utils/firebase";

// utils
import Image from "next/image";
import {
  getSidenav,
  getSidenavFooter,
  getLiquidStakingOptions,
} from "utils/getSidenav";
import { config } from "config/config";
import launchImage from "assets/upgrading-hedera.png";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../styles/globals.css";

// analytics
import { firebaseEvents, trackEvent } from "utils/firebase";
import { EXPLORE_STAKING } from "constants/analytics";
import { FIREBASE_EVENT_PROPERTIES, TELEGRAM_URL } from "constants/constants";

import { useHashConnect, useIsMobile } from "hooks";

import { DefaultScripts } from "app-head";
import Head from "next/head";

const hashConnect = new HashConnectService(new HashConnect(false));
const path = process.env.NEXT_PUBLIC_SUB_ROUTE || "";

const DefaultHead = () => {
  return (
    <Head>
      <DefaultScripts />
    </Head>
  );
};

function MyApp({ Component, pageProps }: AppProps) {
  const [theme, setTheme] = useState<Dict>();
  const [showBanner, setShowBanner] = useState(true);
  const closeBanner = () => setShowBanner(false);
  const isMobile = useIsMobile();
  const { selectedAccount, walletData } = useHashConnect();
  const sendLog = (name: string) => {
    let firebaseRequest: any = {
      [FIREBASE_EVENT_PROPERTIES.EVENT_NAME]: name,
      [FIREBASE_EVENT_PROPERTIES.WALLET_NAME]:
        walletData.pairedWalletData?.name,
      [FIREBASE_EVENT_PROPERTIES.WALLET_ADDRESS]: selectedAccount,
    };
    firebaseEvents(firebaseRequest);
  };
  return config.isLive ? (
    <ChakraProvider theme={theme}>
      <DefaultHead />
      <HandleThemeChange onChange={setTheme} />
      <HashConnectProvider hashConnect={hashConnect} debug>
        <TransactionFeeProvider>
          {!isMobile ? (
            <>
              {showBanner && <BannerWrapper closeBanner={closeBanner} />}
              <PageTemplate
                assetPrefix={path}
                onClick={() => {}}
                header={
                  <Header
                    walletComponent={<ConnectWalletDropdown />}
                    subHeaderItems={getSidenavFooter()}
                  />
                }
                sidenav={getSidenav()}
                liquidStakingOptions={getLiquidStakingOptions()}
                socials={<Socials />}
                trackStaderLogoClick={() => sendLog("Stader_logo")}
                trackLiquidStakingClick={(key: string) =>
                  trackEvent(EXPLORE_STAKING, {
                    next_blockchain: key,
                  })
                }
              >
                <Center>
                  <Box maxW="1280px" width="100%">
                    <Component {...pageProps} />
                  </Box>
                </Center>
              </PageTemplate>
            </>
          ) : (
            <MobileNotSupported exchangeInfo={<ExchangeInfo />} />
          )}
          <SupportButton link={TELEGRAM_URL} />
        </TransactionFeeProvider>
      </HashConnectProvider>
    </ChakraProvider>
  ) : (
    <Box backgroundColor="black">
      <Image src={launchImage} layout="fill" alt=""></Image>
    </Box>
  );
}

export default MyApp;
